import { clients } from '@moovly/clients';
import { Membership, GroupUserRole } from '@moovly/clients/lib/resources/group/group.interfaces';
import { OrganizationMembershipResponse } from '@moovly/clients/lib/resources/organization/organization.interface';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../config/store';
import { toastInfo, toastWarning } from '../utils/ui';
import {
  DefaultApiRequestState,
  getDefaultApiRequestState,
  getDefaultReducerForAsyncRequest,
  RequestType,
} from './default';
import { t } from '@moovly/i18n';

export const updateMembership = createAsyncThunk<
  Membership,
  {
    groupId: string;
    memberId: string;
    role: string;
  }
>('members/update', ({ groupId, memberId, role }) => {
  return clients.Group.updateGroupMembership(groupId, memberId, { role: role as GroupUserRole });
});

export const deleteMember = createAsyncThunk<
  { id: string },
  {
    groupId: string;
    memberId: string;
  }
>('members/delete', async ({ groupId, memberId }) => {
  await clients.Group.removeUserFromGroup(groupId, memberId);
  return { id: memberId };
});

export const addMembers = createAsyncThunk<
  Membership[],
  {
    email?: string;
    firstName?: string;
    lastName?: string;
    userId?: string;
    groupIds: string[];
    organizationId?: string | null;
  }
>('members/add', async ({ email, firstName, lastName, userId, groupIds, organizationId }) => {
  if (organizationId) {
    return clients.Organization.addUserToOrganizationGroups(organizationId, {
      email,
      firstName,
      lastName,
      userId,
      groupIds,
    });
  } else {
    return clients.Group.addUserToGroup(groupIds[0], { email, firstName, lastName, userId }).then((response) => [
      response,
    ]);
  }
});

export const getMembers = createAsyncThunk<
  OrganizationMembershipResponse,
  { groupIds?: string[]; organizationId?: string }
>('members/getMembers', ({ groupIds, organizationId }) => {
  if (organizationId) {
    return clients.Organization.getMembersOrganization(organizationId);
  } else if (groupIds && groupIds.length) {
    return clients.Organization.getMembersGroups(groupIds);
  }
  return {
    memberships: [],
    token: '',
  };
});

type MembersState = {
  memberships: DefaultApiRequestState<
    OrganizationMembershipResponse,
    [RequestType.Get, RequestType.Post, RequestType.Put, RequestType.Delete]
  >;
};

const initialState: MembersState = {
  memberships: getDefaultApiRequestState([RequestType.Get, RequestType.Post, RequestType.Put, RequestType.Delete]),
};

export const membersSlice = createSlice({
  name: 'access',
  initialState,
  reducers: {
    reset: () => initialState,
    resetAddRequest: (state: MembersState) => {
      state.memberships.request.post.status = RequestStatus.Unsent;
    },
  },
  extraReducers: {
    ...getDefaultReducerForAsyncRequest({
      thunk: getMembers,
      requestType: RequestType.Get,
      getInitialState: (state) => state.memberships,
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: updateMembership,
      requestType: RequestType.Put,
      getInitialState: (state) => state.memberships,
      onSuccess: (state, payload: Membership) => {
        const index = state.data.memberships.findIndex((m: Membership) => m.id === payload.id);
        state.data.memberships[index] = payload;
      },
      onError: (state, payload) => {
        toastWarning(payload.message);
      },
    }),

    ...getDefaultReducerForAsyncRequest({
      thunk: addMembers,
      requestType: RequestType.Post,
      getInitialState: (state) => state.memberships,
      onSuccess: (state, payload: Membership[]) => {
        state.data.memberships = [...(state.data.memberships || []), ...payload];
        toastInfo(t('alerts.user.added'));
      },
      onError: (state, payload) => {
        toastWarning(payload.message);
      },
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: deleteMember,
      requestType: RequestType.Delete,
      getInitialState: (state) => state.memberships,
      onSuccess: (state, payload: { id: string }) => {
        state.data.memberships = state.data.memberships.filter((m: Membership) => m.id !== payload.id);
      },
      onError: (state, payload) => {
        toastWarning(payload.message);
      },
    }),
  },
});

export default membersSlice;
